import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK } from './agencyChartTheme';

export interface IInsightsTile {
    id: string;
    value: number;
    label: string;
    isOn: boolean;
    /** True for a tile that opens a panel rather than narrowing, which is a different aria state. */
    expands?: boolean;
    /** Absent for a tile with nothing behind it, which then reads rather than invites a press. */
    onClick?: () => void;
}

const useStyles = createUseStyles({
    tiles: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))',
        gap: 12
    },

    tile: {
        display: 'block',
        width: '100%',
        padding: '12px 14px',
        borderRadius: 11,
        border: `1px solid ${BRAND.tealEdge}`,
        background: BRAND.tealWash,
        font: 'inherit',
        textAlign: 'left' as const
    },

    /** The affordances of a control, kept off the readout so it does not invite a press. */
    pressable: {
        cursor: 'pointer',

        '&:hover': {
            borderColor: BRAND.deepTeal
        }
    },

    tileOn: {
        borderColor: BRAND.coral,
        background: BRAND.coralWash,

        '&:hover': {
            borderColor: BRAND.coral
        }
    },

    value: {
        display: 'block',
        fontSize: 26,
        fontWeight: 600,
        lineHeight: 1.15,
        color: BRAND.deepTeal,
        fontVariantNumeric: 'tabular-nums'
    },

    label: {
        display: 'block',
        marginTop: 2,
        fontSize: 12.5,
        color: CHART_INK.label,
        lineHeight: 1.4
    }
});

/** The headline numbers, each of them also the way into the detail behind it. */
export const InsightsTiles: FC<{ tiles: IInsightsTile[] }> = ({ tiles }) => {
    const classes = useStyles();

    return (
        <div className={classes.tiles}>
            {tiles.map(tile => {
                const content = (
                    <>
                        <span className={classes.value} data-id="insights-tile-value">{tile.value}</span>
                        <span className={classes.label}>{tile.label}</span>
                    </>
                );

                // A tile with nothing behind it is a readout, not a control. Rendered as a button
                // it offers a press that cannot do anything, and announces a pressed state to a
                // screen reader for a selection that does not exist.
                if (!tile.onClick) {
                    return (
                        <div key={tile.id} className={classes.tile} data-id={`insights-tile-${tile.id}`}>
                            {content}
                        </div>
                    );
                }

                return (
                    <button
                        key={tile.id}
                        type="button"
                        className={`${classes.tile} ${classes.pressable} ${tile.isOn ? classes.tileOn : ''}`}
                        data-id={`insights-tile-${tile.id}`}
                        aria-pressed={tile.expands ? undefined : tile.isOn}
                        aria-expanded={tile.expands ? tile.isOn : undefined}
                        onClick={tile.onClick}
                    >
                        {content}
                    </button>
                );
            })}
        </div>
    );
};
